"use client";

import { FC } from "react";
import { StockItem } from "../../types/stocks";
import { BUDGET_ETF_COLORS, BUDGET_ETF_ICONS } from "./BudgetETFTypes";
import { formatBudgetEtfChange, formatBudgetEtfLtp } from "./budgetEtfUtils";

interface BudgetEtfListRowProps {
  stock: StockItem;
  onClick?: (stock: StockItem) => void;
  isRowClickable?: boolean;
}

const BudgetEtfListRow: FC<BudgetEtfListRowProps> = ({
  stock,
  onClick,
  isRowClickable = true,
}) => {
  const isPositive = Number(stock?.perChange) >= 0;
  const trendColorClass = isPositive
    ? BUDGET_ETF_COLORS.positiveTrend
    : BUDGET_ETF_COLORS.negativeTrend;
  const trendIconClass = isPositive
    ? BUDGET_ETF_ICONS.trendUp
    : BUDGET_ETF_ICONS.trendDown;

  const rowClassName = `border-b ${BUDGET_ETF_COLORS.border} py-3 ${
    isRowClickable ? "cursor-pointer" : "cursor-default"
  }`;

  const rowContent = (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3">
      <div className="min-w-0">
        <p
          className={`truncate text-[14px] font-medium uppercase md:text-[16px] ${BUDGET_ETF_COLORS.primaryText}`}
        >
          {stock?.symbol}
        </p>
        <p
          className={`truncate text-[12px] md:text-[14px] ${BUDGET_ETF_COLORS.secondaryText}`}
        >
          {stock?.fullName}
        </p>
      </div>

      <div className="flex min-w-[110px] flex-col items-end">
        <div className="flex items-center gap-1">
          <span
            className={`text-[14px] font-normal md:text-[16px] ${trendColorClass}`}
          >
            {formatBudgetEtfLtp(stock?.ltp)}
          </span>
          <i
            className={`icomoon text-[12px] ${trendColorClass} ${trendIconClass}`}
            aria-hidden="true"
          />
        </div>
        <p className={`text-[12px] md:text-[14px] text-gray-600`}>
          {formatBudgetEtfChange(stock?.changePrice, stock?.perChange)}
        </p>
      </div>
    </div>
  );

  if (isRowClickable) {
    return (
      <button
        type="button"
        onClick={() => onClick?.(stock)}
        className={`w-full border-0 bg-transparent p-0 text-left ${rowClassName}`}
        aria-label={`View details for ${stock?.fullName ?? stock?.symbol ?? "ETF"}`}
      >
        {rowContent}
      </button>
    );
  }

  return <div className={rowClassName}>{rowContent}</div>;
};

export default BudgetEtfListRow;
